// playbookDepth — shared depth-toggle logic for any playbook surface
// (quest playbooks, generate-guide outputs, future surfaces).
//
// Per the product call: depth controls *render*, not *generation*. We
// keep the full payload from the AI and just decide which fields are
// visible / which steps auto-expand at each level. That means switching
// is instant, free, and never re-streams the playbook.
//
// Auto-pick rule:
//   - difficulty 1-2  → "short"     (just enough to act)
//   - difficulty 3    → "detailed"  (default — full guidance)
//   - difficulty 4-5  → "expert"    (everything + auto-expand runners)
// User override (per-surface key + global fallback) is remembered in
// localStorage so a learner who always wants Expert keeps it sticky.
import { useCallback, useEffect, useState } from "react";

export type PlaybookDepth = "short" | "detailed" | "expert";

export const PLAYBOOK_DEPTHS: PlaybookDepth[] = ["short", "detailed", "expert"];

const GLOBAL_KEY = "hl.playbook.depth";
const surfaceKey = (surface: string) => `hl.playbook.depth.${surface}`;

/** Map a 1-5 difficulty (or string label) to a default depth. */
export const defaultDepthForDifficulty = (
  difficulty: number | string | null | undefined,
): PlaybookDepth => {
  if (typeof difficulty === "string") {
    const d = difficulty.trim().toLowerCase();
    if (d === "beginner" || d === "easy") return "short";
    if (d === "advanced" || d === "expert" || d === "hard") return "expert";
    return "detailed";
  }
  if (typeof difficulty !== "number" || !Number.isFinite(difficulty)) {
    return "detailed";
  }
  if (difficulty <= 2) return "short";
  if (difficulty >= 4) return "expert";
  return "detailed";
};

/** Pull the saved depth for this surface, falling back to the global
 *  override, then to the auto-derived default. */
export const readSavedDepth = (
  surface: string,
): PlaybookDepth | null => {
  if (typeof window === "undefined") return null;
  try {
    const perSurface = window.localStorage.getItem(surfaceKey(surface));
    if (perSurface && PLAYBOOK_DEPTHS.includes(perSurface as PlaybookDepth)) {
      return perSurface as PlaybookDepth;
    }
    const global = window.localStorage.getItem(GLOBAL_KEY);
    if (global && PLAYBOOK_DEPTHS.includes(global as PlaybookDepth)) {
      return global as PlaybookDepth;
    }
  } catch {
    // localStorage blocked — fall through to default.
  }
  return null;
};

interface UseDepthArgs {
  /** Stable id (e.g. quest slug, "hacker-guide") used for the per-surface
   *  override key. Different surfaces remember their depth independently. */
  surface: string;
  /** Quest difficulty (1-5) or label string ("Beginner" / "Advanced"). */
  difficulty?: number | string | null;
}

/** Stateful hook: returns the current depth + setter that persists. */
export const usePlaybookDepth = ({ surface, difficulty }: UseDepthArgs) => {
  const [depth, setDepthState] = useState<PlaybookDepth>(() => {
    return readSavedDepth(surface) ?? defaultDepthForDifficulty(difficulty);
  });

  // If the difficulty changes (e.g. switching quests in the same shell)
  // and the user hasn't picked an override for this surface, follow the
  // new auto-default.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const hasOverride =
        !!window.localStorage.getItem(surfaceKey(surface)) ||
        !!window.localStorage.getItem(GLOBAL_KEY);
      if (!hasOverride) {
        setDepthState(defaultDepthForDifficulty(difficulty));
      }
    } catch {
      // ignore
    }
  }, [surface, difficulty]);

  const setDepth = useCallback(
    (next: PlaybookDepth) => {
      setDepthState(next);
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(surfaceKey(surface), next);
        // Also update the global so the user's preferred depth carries
        // across new surfaces they haven't explicitly toggled yet.
        window.localStorage.setItem(GLOBAL_KEY, next);
      } catch {
        // ignore
      }
    },
    [surface],
  );

  return { depth, setDepth };
};

/** Pure render-time helper: which step sub-blocks should be visible at
 *  this depth. Keeps QuestWorkshop / HackerGuide rendering declarative. */
export interface DepthVisibility {
  showDescription: boolean;
  showTool: boolean;
  showExamplePrompt: boolean;
  showProTip: boolean;
  showAlternatives: boolean;
  /** Whether step runners / step bodies should auto-expand on first paint. */
  autoExpandSteps: boolean;
  /** Whether to render long-form bonus content (bonus tips, success criteria). */
  showBonus: boolean;
}

export const visibilityForDepth = (depth: PlaybookDepth): DepthVisibility => {
  switch (depth) {
    case "short":
      return {
        showDescription: true,
        showTool: true,
        showExamplePrompt: false,
        showProTip: false,
        showAlternatives: false,
        autoExpandSteps: false,
        showBonus: false,
      };
    case "expert":
      return {
        showDescription: true,
        showTool: true,
        showExamplePrompt: true,
        showProTip: true,
        showAlternatives: true,
        autoExpandSteps: true,
        showBonus: true,
      };
    case "detailed":
    default:
      return {
        showDescription: true,
        showTool: true,
        showExamplePrompt: true,
        showProTip: true,
        showAlternatives: true,
        autoExpandSteps: false,
        showBonus: true,
      };
  }
};

/** Short label + tooltip for the toggle UI. */
export const DEPTH_META: Record<
  PlaybookDepth,
  { label: string; hint: string }
> = {
  short: {
    label: "Short",
    hint: "Just the steps — minimal guidance.",
  },
  detailed: {
    label: "Detailed",
    hint: "Full playbook with prompts, tools, and pro tips.",
  },
  expert: {
    label: "Expert",
    hint: "Everything expanded, runners ready, alternatives shown.",
  },
};